import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getVendorProducts,
  deleteVendorProduct,
  toggleProductPublish,
} from '../../redux/slices/vendorProductSlice';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const VendorProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { products, loading, error } = useSelector((state) => state.vendorProducts);

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    search: searchParams.get('search') || '',
    sortBy: searchParams.get('sortBy') || 'createdAt',
  });

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    dispatch(getVendorProducts(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    Object.keys(newFilters).forEach((k) => {
      if (newFilters[k] && newFilters[k] !== 'all') {
        params.set(k, newFilters[k]);
      }
    });
    setSearchParams(params);
  };

  const handleDelete = async (productId) => {
    try {
      await dispatch(deleteVendorProduct(productId)).unwrap();
      toast.success('Product deleted successfully');
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete product');
    }
  };

  const handleTogglePublish = async (productId) => {
    try {
      await dispatch(toggleProductPublish(productId)).unwrap();
      toast.success('Product status updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update product');
    }
  };

  const getStatusBadge = (product) => {
    if (product.approvalStatus === 'pending') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">Pending Approval</span>;
    }
    if (product.approvalStatus === 'rejected') {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Rejected</span>;
    }
    if (!product.isPublished) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Draft</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Published</span>;
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Products</h1>
          <p className="text-gray-600 mt-1">Manage your product catalog</p>
        </div>
        <Link
          to="/vendor/products/new"
          className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-800"
        >
          <Plus className="w-5 h-5" />
          Add Product
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Sort */}
          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="createdAt">Newest First</option>
            <option value="name">Name (A-Z)</option>
            <option value="price">Price (Low to High)</option>
            <option value="stock">Stock (Low to High)</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No Products Found</h3>
          <p className="text-gray-600 mb-4">Start by adding your first product</p>
          <Link
            to="/vendor/products/new"
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {products.map((product) => (
            <div
              key={product._id}
              className="bg-white rounded-lg shadow hover:shadow-md transition p-4 flex items-center gap-4"
            >
              {/* Product Image */}
              <img
                src={product.images[0]?.url}
                alt={product.name}
                className="w-24 h-24 object-cover rounded"
              />

              {/* Product Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-lg">{product.name}</h3>
                  {getStatusBadge(product)}
                </div>
                <p className="text-gray-600 text-sm mb-2">SKU: {product.sku}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-semibold">₹{product.price}</span>
                  <span className="text-gray-600">Stock: {product.countInStock}</span>
                  <span className="text-gray-600">
                    Sizes: {product.sizes.join(', ')}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {product.approvalStatus === 'approved' && (
                  <button
                    onClick={() => handleTogglePublish(product._id)}
                    className={`px-4 py-2 rounded flex items-center gap-2 text-sm ${
                      product.isPublished
                        ? 'bg-gray-100 hover:bg-gray-200'
                        : 'bg-green-100 hover:bg-green-200 text-green-700'
                    }`}
                  >
                    {product.isPublished ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Unpublish
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Publish
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={() => navigate(`/vendor/products/${product._id}/edit`)}
                  className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded flex items-center gap-2 text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>

                <button
                  onClick={() => setDeleteConfirm(product._id)}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded flex items-center gap-2 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md">
            <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this product? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorProducts;
